import { useEffect, useState } from 'react'

import { PhoneNumberSearchInput } from '@forwardflow/features/phone-numbers/components/PhoneNumberSearchInput'
import { Button } from '@forwardflow/ui'
import { Spinner } from '@forwardflow/ui'

import {
   InviteAgentModal,
   RemovePhoneNumberModal,
   ReplacePhoneNumberModal,
} from '@forwardflow/modals'

import { UserListItem } from '../UserListItem'
import { useOrganizationUsersInfinite } from '@forwardflow/hooks/customHooks/organization'
import { useInviteAgentModalStore } from '@forwardflow/store/modal'
import OrganizationService from '@forwardflow/services/OrganizationService'
import { message } from 'antd'
import { InfiniteScroll } from '@forwardflow/ui-v2'
// import { useGetUserSubscription } from '@forwardflow/hooks/customHooks/subscription/useGetUserSubscription'

// Helper function to initialize user permissions from isWhiteListed field
const initializeUserPermissions = (users: any[]): Record<string, 'yes' | 'no' | null> => {
   const permissions: Record<string, 'yes' | 'no' | null> = {}
   users.forEach((user: any) => {
      if (user.email) {
         if (typeof user.isWhiteListed === 'boolean') {
            permissions[user.email] = user.isWhiteListed ? 'yes' : 'no'
         } else {
            permissions[user.email] = null
         }
      }
   })
   return permissions
}

const UserManagement = () => {
   const { openInviteAgentModal, isInviteAgentModalOpen } =
      useInviteAgentModalStore()

   const acceptedHook = useOrganizationUsersInfinite({ invitationStatus: 'accepted' })
   const pendingHook = useOrganizationUsersInfinite({ invitationStatus: 'pending' })
      // const { data: subscriptionData } = useGetUserSubscription()

   // const totalSeatCount = subscriptionData?.organizationSubscription?.seatCounts ?? 0
   // const totalUsers = acceptedHook.totalItems + pendingHook.totalItems

   const [selectedPhoneNumbers, setSelectedPhoneNumbers] = useState<number[]>(
      []
   )
   const [searchQuery, setSearchQuery] = useState('')
   const [isEditingWhitelist, setIsEditingWhitelist] = useState(false)

   const allUsers = [...acceptedHook.users, ...pendingHook.users]

   const filteredUsers = allUsers.filter(
      (user) =>
         user.firstName?.toLowerCase().includes(searchQuery?.toLowerCase()) ||
         user.email?.toLowerCase().includes(searchQuery?.toLowerCase())
   )

   const filteredAcceptedUsers = filteredUsers.filter(
      (user) => user.invitationStatus === 'accepted'
   )
   const filteredPendingUsers = filteredUsers.filter(
      (user) => user.invitationStatus === 'pending'
   )

   const whitelistedCount = acceptedHook.users.filter(
      (user) => user.isWhiteListed === true
   ).length

   const [userPermissions, setUserPermissions] = useState<
      Record<string, 'yes' | 'no' | null>
   >({})

   const { wasInviteSent } = useInviteAgentModalStore()

   useEffect(() => {
      if (!isInviteAgentModalOpen && wasInviteSent) {
         acceptedHook.refetch()
         pendingHook.refetch()
      }
   }, [isInviteAgentModalOpen])

   useEffect(() => {
      setUserPermissions(initializeUserPermissions(allUsers))
   }, [acceptedHook.users, pendingHook.users])

   const handleSelectionChange = (id: number, isSelected: boolean) => {
      setSelectedPhoneNumbers((prev) =>
         isSelected ? [...prev, id] : prev.filter((num) => num !== id)
      )
   }
   const handlePermissionChange = (
      userId: string,
      permission: 'yes' | 'no' | null
   ) => {
      setUserPermissions((prev) => ({
         ...prev,
         [userId]: permission,
      }))
   }

   const handleSubmitAllUsers = async () => {
      const whitelistedEmails = Object.entries(userPermissions)
         .filter(([, permission]) => permission === 'yes')
         .map(([email]) => email)
         .filter((email) => email)

      const nonWhitelistedEmails = Object.entries(userPermissions)
         .filter(([, permission]) => permission === 'no')
         .map(([email]) => email)
         .filter((email) => email)

      // Prepare API payloads
      const whitelistPayload = {
         emails: whitelistedEmails,
         isWhiteListed: true,
      }

      const nonWhitelistPayload = {
         emails: nonWhitelistedEmails,
         isWhiteListed: false,
      }

      try {
         if (whitelistedEmails.length > 0) {
            await OrganizationService.patchWhitelistAPI(whitelistPayload)
         }
         if (nonWhitelistedEmails.length > 0) {
            await OrganizationService.patchWhitelistAPI(nonWhitelistPayload)
         }
         message.success('Whitelist updated successfully')
      } catch (error) {
         console.error('Error updating whitelist:', error)
         message.error('Failed to update whitelist')
      } finally {
         acceptedHook.refetch()
         pendingHook.refetch()
      }
   }

   const handleEditWhitelist = () => {
      setIsEditingWhitelist(true)
   }

   const handleCancelEdit = () => {
      setIsEditingWhitelist(false)
      setUserPermissions(initializeUserPermissions(allUsers))
   }

   const handleDoneEdit = () => {
      handleSubmitAllUsers()
      setIsEditingWhitelist(false)
   }

   // Render user lists component
   const renderUserLists = () => (
      <>
         <PhoneNumberSearchInput
            placeholder="Search Users"
            value={searchQuery}
            onChange={(value) => setSearchQuery(value)}
            className="!max-w-full"
         />

         {(acceptedHook.isLoading || pendingHook.isLoading) && acceptedHook.users.length === 0 && pendingHook.users.length === 0 ? (
            <div className="flex flex-col gap-y-1 px-4 pt-4">
               {[...Array(5)].map((_, i) => (
                  <div
                     key={i}
                     className="flex items-center h-[62px] pl-10 bg-primary/10 rounded animate-pulse"
                  >
                     <div className="w-10 h-10 rounded-full bg-white/10 mr-4 shrink-0" />
                     <div className="flex flex-col gap-1.5 flex-1 mr-4">
                        <div className="h-3.5 w-32 bg-white/10 rounded" />
                        <div className="h-3 w-44 bg-white/[0.06] rounded" />
                     </div>
                  </div>
               ))}
            </div>
         ) : (
         <InfiniteScroll
            id="user-management-scroll"
            isLoading={acceptedHook.isLoading || pendingHook.isLoading}
            dataLength={acceptedHook.users.length + pendingHook.users.length}
            next={() =>
               acceptedHook.hasMore
                  ? acceptedHook.loadMore()
                  : pendingHook.loadMore()
            }
            hasMore={acceptedHook.hasMore || pendingHook.hasMore}
            loader={
               <div className="flex justify-center py-4">
                  <Spinner />
               </div>
            }
            scrollThreshold={0.8}
         >
            <div className="flex text-[11px] w-full items-center justify-between px-10 pt-4 pb-2 text-white/40 uppercase tracking-wider">
               <span>Active Users ({filteredAcceptedUsers.length})</span>
               {!isEditingWhitelist && whitelistedCount > 0 && (
                  <span>{whitelistedCount} Whitelisted</span>
               )}
            </div>
            <div className="flex flex-col gap-y-1">
               {filteredAcceptedUsers?.length > 0 ? (
                  filteredAcceptedUsers.map((item, index) => (
                     <UserListItem
                        email={item.email}
                        isAdded={false}
                        hideActionButton={isEditingWhitelist}
                        key={index}
                        userId={item.email}
                        name={item.firstName + ' ' + item.lastName}
                        role={item.roles?.[0] || ''}
                        onSelectionChange={(isSelected) =>
                           handleSelectionChange(item.id, isSelected)
                        }
                        isSelected={selectedPhoneNumbers.includes(item.id)}
                        onPermissionChange={handlePermissionChange}
                        defaultPermission={userPermissions[item.email]}
                        isWhitelist={item.isWhiteListed}
                        showPermissionButtons={isEditingWhitelist}
                        linkedEmail={item.linkedEmail}
                     />
                  ))
               ) : (
                  <p className="text-white/30 text-[13px] text-center py-6">
                     {searchQuery ? 'No matching users' : 'Invite your team to get started'}
                  </p>
               )}
            </div>

            {filteredPendingUsers?.length > 0 && (
               <>
                  <div className="flex text-[11px] w-full items-center px-10 pt-5 pb-2 text-white/40 uppercase tracking-wider">
                     <span>Pending Invitations ({filteredPendingUsers.length})</span>
                  </div>
                  <div className="flex flex-col gap-y-1">
                     {filteredPendingUsers.map((item, index) => (
                        <UserListItem
                           isPending
                           email={item.email}
                           isAdded={false}
                           hideActionButton={isEditingWhitelist}
                           key={index}
                           userId={item.email}
                           name={item.firstName + ' ' + item.lastName}
                           role={item.roles?.[0] || 'Admin'}
                           onSelectionChange={(isSelected) =>
                              handleSelectionChange(item.id, isSelected)
                           }
                           isSelected={selectedPhoneNumbers.includes(item.id)}
                           onPermissionChange={handlePermissionChange}
                           defaultPermission={userPermissions[item.email]}
                           isWhitelist={item.isWhiteListed}
                           showPermissionButtons={isEditingWhitelist}
                           linkedEmail={item.linkedEmail}
                        />
                     ))}
                  </div>
               </>
            )}
         </InfiniteScroll>
         )}
      </>
   )

   return (
      <div className="flex flex-col flex-1 h-full overflow-hidden">
         <div className="flex items-center justify-between h-20 pl-8 pr-4 bg-white/5 shrink-0">
            <span className="text-lg font-medium">User Management</span>
            {!isEditingWhitelist ? (
               <div className="flex items-center gap-4">
                  <Button
                     onClick={handleEditWhitelist}
                     className="!h-10 !rounded bg-white/10 hover:bg-white/15 !border-white/10 px-7 !mt-0 !w-40 whitespace-nowrap"
                  >
                     Edit Whitelist
                  </Button>
                  <Button
                     onClick={openInviteAgentModal}
                     className="!h-10 !rounded bg-primary/10 hover:bg-primary/15 border-primary px-7 !mt-0 max-w-40"
                  >
                     Add User
                  </Button>
               </div>
            ) : (
               <div className="flex items-center gap-3">
                  <span className="text-white/50 text-sm mr-2">
                     Editing Whitelist
                  </span>
                  <Button
                     className="!h-10 !rounded bg-white/10 hover:bg-white/15 !border-white/10 px-7 !mt-0 !w-28"
                     onClick={handleCancelEdit}
                  >
                     Cancel
                  </Button>
                  <Button
                     className="!rounded !w-28 !mt-0 !h-10"
                     onClick={handleDoneEdit}
                  >
                     Done
                  </Button>
               </div>
            )}
         </div>
         <div className="flex-1 overflow-y-auto p-6">
            {renderUserLists()}
         </div>

         <InviteAgentModal />
         <ReplacePhoneNumberModal />
         <RemovePhoneNumberModal />
      </div>
   )
}
export default UserManagement
